import { IPromise } from 'angular';
import { call, put, select } from 'redux-saga/effects';
import * as _ from 'underscore';

import { Messages } from '../../../../codegen/Localize';
import { DatasourceDefinition, WorkbookDefinition } from '../../../../ts/models/ContentDefinitions';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IDataSourceBase, IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import {
  IWorkbookBase, IWorkbookInfo,
} from '../../../../ts/models/IWorkbookInfo';
import { Set } from '../../../../ts/models/Set';
import { IResult } from '../../../../ts/ServerApi/types/IResponse';
import {
  CreateExtractTasksAction,
  CreateExtractTasksMode
} from '../../../../ts/services/CreateExtractTasksAction';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { CreateExtractRefreshAction } from '../../../actions/spaces/CreateExtractRefreshAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { selectSpaceInfo } from '../../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { SpaceInfo } from '../../../state/SpaceInfo';
import { getDatasourceInfos, getWorkbookInfos } from '../SpacesSagaHelpers';
import { RefreshSelfServiceScheduleDialog, RefreshSelfServiceScheduleDialogState } from '../../../content/RefreshSelfServiceScheduleDialog';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { ErrorNotifyingServer } from '../../../../ts/services/ErrorNotifyingServer';
import { CreateExtractTasksSelfServiceSchedulesRequest, RefreshTargetType, RefreshTaskType } from '../../../../ts/ServerApi/requests/CreateSelfServiceScheduleExtractTasksRequest';
import { Recurrence } from '@tableau/self-service-schedules';
import { SelfServiceSchedule } from '../../../../ts/ServerApi/types/SelfServiceSchedule';
import { SessionSiteSelectors } from '../../../selectors/SessionInfoSelectors';
import { appStore } from '../../../AppStore';

export function openWorkbookRefreshExtractDialog(
  workbooks: Set<IWorkbookBase>,
  mode: CreateExtractTasksMode
): IPromise<IResult> {
  const context = ServiceLocator.getAngularActionContext();
  const createExtractModule = ServiceLocator.get<CreateExtractTasksAction>('CreateExtractTasksAction');
  return createExtractModule.forWorkbooks(workbooks, context, mode);
}

export function openDatasourceRefreshExtractDialog(
  datasources: Set<IDataSourceBase>,
  mode: CreateExtractTasksMode
): IPromise<IResult> {
  const createExtractModule = ServiceLocator.get<CreateExtractTasksAction>('CreateExtractTasksAction');
  const context = ServiceLocator.getAngularActionContext();
  return createExtractModule.forDatasources(datasources, context, mode);
}

export function openContentRefreshExtractDialog(
  content: Set<IContentInfo>,
  mode: CreateExtractTasksMode
): IPromise<IResult> {
  const createExtractModule = ServiceLocator.get<CreateExtractTasksAction>('CreateExtractTasksAction');
  const context = ServiceLocator.getAngularActionContext();
  return createExtractModule.forContent(content, context, mode);
}

export function createSelfServiceScheduleExtractAction(action: CreateExtractRefreshAction) {
  type ResourceInfo = IDataSourceInfo | IWorkbookInfo;
  const targetResources = action.selectedContent as ResourceInfo[];

  // Target Type
  const capitalizeString = (s: string) => s[0].toUpperCase() + s.substring(1);
  const targetTypes = targetResources.map(content => content.contentType).map(capitalizeString);
  const allSameTargetType = _.unique(targetTypes).length === 1;
  const targetType = allSameTargetType
    ? targetTypes[0] as RefreshTargetType
    : 'Mixed';

  // Task Type
  const canSupportIncremental = targetResources.map(target => target.hasIncrementalExtract);
  const allowIncremental = _.all(canSupportIncremental);

  const quantity = action.selectedContent.length;
  const name = quantity === 1
    ? action.selectedContent[0].name
    : undefined;
  const dummySubmit = (recurrence: Recurrence, taskType: RefreshTaskType, scheduleName: string): Promise<IResult> => {
    const server = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const request = new CreateExtractTasksSelfServiceSchedulesRequest({
      schedule: SelfServiceSchedule.fromRecurrence(recurrence, 'Extract'),
      scheduleName: scheduleName.substring(0, 255),
      targetIds: action.selectedContent.map(content => Number(content.id)),
      targetType,
      type: taskType,
    });
    return new Promise<IResult>((resolve, reject) => {
      server.sendRequest(request).then(result => {
        CreateExtractRefreshAction.notifyResult(result);
        resolve(result);
      }).catch(err => {
        CreateExtractRefreshAction.notifyResult();
        reject(err);
      });
    });
  };
  const $q = ServiceLocator.get<ng.IQService>('$q');
  return $q.resolve(RefreshSelfServiceScheduleDialog.open({
    quantity, targetType, name,
    save: dummySubmit,
    isNew: true,
    taskType: allowIncremental ? 'IncrementExtract' : 'RefreshExtract',
    disableSelectingTaskType: !allowIncremental,
  }));
}

export function* handleCreateExtractRefreshAction(action: CreateExtractRefreshAction) {
  const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
  const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(appStore.getState());
  if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite) {
    yield call(createSelfServiceScheduleExtractAction, action);
    yield put(requestSpaceContents());
} else {
    const localizedErrorMessage = Messages.unexpectedError();
    try {
      const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
      const typeArray = Object.keys(contentDictionaryByType);

      if (typeArray.length === 1) {
        const singleContentType = typeArray[0];
        switch (singleContentType) {
          case WorkbookDefinition.contentType:
            const workbookInfos = yield call(getWorkbookInfos, contentDictionaryByType[singleContentType]);
            const workbookSet = Set.fromIdItemArray(workbookInfos);
            yield call(openWorkbookRefreshExtractDialog, workbookSet, action.mode);
            break;
          case DatasourceDefinition.contentType:
            const datasourceInfos = yield call(getDatasourceInfos, contentDictionaryByType[singleContentType]);
            const datasourceSet = Set.fromIdItemArray(datasourceInfos);
            yield call(openDatasourceRefreshExtractDialog, datasourceSet, action.mode);
            break;
          default:
            yield put(requestFailed(`${singleContentType} does not support extract refresh.`));
            break;
        }
      } else if (typeArray.length > 1) {
        const workbookInfos = yield call(getWorkbookInfos, contentDictionaryByType[WorkbookDefinition.contentType]);
        const datasourceInfos = yield call(getDatasourceInfos, contentDictionaryByType[DatasourceDefinition.contentType]);
        const contentIds = Set.fromIdItemArray(workbookInfos.concat(datasourceInfos));
        yield call(openContentRefreshExtractDialog, contentIds, action.mode);
      }

      const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
      if (spaceInfo.subspaceName === 'extractTasks' || spaceInfo.subspaceName === 'extractRefreshes') {
        yield put(requestSpaceContents());
      }
    } catch (e) {
      yield put(requestFailed(e, localizedErrorMessage));
    }
  }
}
